import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  TextField,
  useTheme,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as LoanIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import {
  RefreshCw,
  Calendar,
  ClipboardList,
  Clock,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import apiService from '../Transactions/api';

// The 3 status cards that replace the old "Pending / Classified" pair and
// the status <select>. Cards filter the table client-side; "All Outgoing
// Payments" is the default.
const STATUS_CARDS = [
  { key: 'all', label: 'All Outgoing Payments', icon: ClipboardList },
  { key: 'classified', label: 'Classified', icon: CheckCircle2 },
  { key: 'unclassified', label: 'Unclassified', icon: Clock },
];

const matchesCard = (purpose, cardKey) => {
  switch (cardKey) {
    case 'all': return true;
    case 'unclassified': return purpose === 'pending_classification';
    case 'classified': return purpose !== 'pending_classification';
    default: return false;
  }
};

// Tailwind accent classes per card, reused for the card icon and for the
// card's active/selected state — same convention as the Loans/Members/
// Groups pages.
const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  classified: {
    icon: (isDark) => (isDark ? 'text-green-400' : 'text-green-600'),
    active: (isDark) => (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'),
  },
  unclassified: {
    icon: (isDark) => (isDark ? 'text-orange-400' : 'text-orange-600'),
    active: (isDark) => (isDark ? 'border-orange-500 bg-orange-900/20' : 'border-orange-500 bg-orange-50'),
  },
};

// Default date range: past 45 days, as requested. "Clear Filter" (in the
// month menu / custom date dialog) resets back to no date restriction.
const getPast45DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 45);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const ConsolidatedWithdrawals = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showClassifyDialog, setShowClassifyDialog] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [cardFilter, setCardFilter] = useState('all'); // which card is active

  const [dateRange, setDateRange] = useState(getPast45DaysRange());
  // Tracks which single date control is "active" so exactly one of
  // Past 45 Days / Select Month / Custom is highlighted at a time.
  const [activeDatePreset, setActiveDatePreset] = useState('past45'); // 'past45' | 'month' | 'custom' | 'all'
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  const [classificationData, setClassificationData] = useState({
    classification_type: '',
    loan_id: null,
    withdrawal_request_id: null,
    notes: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // The card is a client-side filter now, not a server-side one — we need
  // every purpose in hand at once to show all 3 card counts, so only the
  // date range and page trigger a re-fetch.
  useEffect(() => {
    loadTransactions();
  }, [dateRange, pagination.page]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (dateRange.startDate) {
        filterParams.start_date = dateRange.startDate;
      }
      if (dateRange.endDate) {
        filterParams.end_date = dateRange.endDate;
      }

      const response = await apiService.fetchBankToClientTransactions(filterParams);

      setTransactions(response.data || []);

      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        }));
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setActiveDatePreset('all');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePast45Days = () => {
    setDateRange(getPast45DaysRange());
    setSelectedMonth('');
    setActiveDatePreset('past45');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getPurposeLabel = (purpose) => {
    const labels = {
      pending_classification: 'Pending Classification',
      loan_disbursement: 'Long Term Loan Disbursement',
      advance_disbursement: 'Advance Loan Disbursement',
      client_withdrawal: 'Savings Withdrawal',
      refund: 'Refund',
      emergency_payout: 'Emergency Payout',
      other: 'Other',
    };
    return labels[purpose] || purpose;
  };

  const getPurposeColor = (purpose) => {
    const colors = {
      pending_classification: '#ff9800',
      loan_disbursement: '#2196f3',
      advance_disbursement: '#9c27b0',
      client_withdrawal: '#00bcd4',
      refund: '#4caf50',
      emergency_payout: '#f44336',
      other: '#757575',
    };
    return colors[purpose] || '#757575';
  };

  const handleOpenClassifyDialog = async (transaction) => {
    setSelectedTransaction(transaction);
    setClassificationData({
      classification_type: '',
      loan_id: null,
      withdrawal_request_id: null,
      notes: '',
    });
    setShowClassifyDialog(true);

    // Load suggestions
    await loadClassificationSuggestions(transaction.id);
  };

  const loadClassificationSuggestions = async (transactionId) => {
    try {
      setLoadingSuggestions(true);
      const response = await apiService.getClassificationSuggestions(transactionId);
      setSuggestions(response);

      // Auto-select classification if there's an exact match
      if (response.ready_for_action.loans.length > 0 &&
          response.ready_for_action.loans[0].match_status === 'exact_match') {
        setClassificationData(prev => ({
          ...prev,
          classification_type: 'loan_disbursement',
          loan_id: response.ready_for_action.loans[0].loan_id,
        }));
      } else if (response.ready_for_action.withdrawals.length > 0 &&
                 response.ready_for_action.withdrawals[0].match_status === 'exact_match') {
        setClassificationData(prev => ({
          ...prev,
          classification_type: 'client_withdrawal',
          withdrawal_request_id: response.ready_for_action.withdrawals[0].request_id,
        }));
      }
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setSuggestions(null);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleClassify = async () => {
    if (!classificationData.classification_type) {
      alert('Please select a classification type');
      return;
    }

    // Validate required fields
    if (classificationData.classification_type === 'loan_disbursement' && !classificationData.loan_id) {
      alert('Please select a loan');
      return;
    }

    if (classificationData.classification_type === 'client_withdrawal' && !classificationData.withdrawal_request_id) {
      alert('Please select a withdrawal request');
      return;
    }

    try {
      setClassifying(true);

      const response = await apiService.classifyTransaction(
        selectedTransaction.id,
        classificationData
      );

      if (response.success) {
        alert(response.message || 'Transaction classified successfully!');

        // Reload transactions
        await loadTransactions();

        setShowClassifyDialog(false);
        setSelectedTransaction(null);
        setSuggestions(null);
      } else {
        alert(response.error || 'Failed to classify transaction');
      }
    } catch (error) {
      console.error('Error classifying transaction:', error);

      // Show specific error if available
      if (error.response?.data?.warning) {
        alert(`Warning: ${error.response.data.warning}\n\n${error.response.data.error}`);
      } else {
        alert(error.message || 'Failed to classify transaction. Please try again.');
      }
    } finally {
      setClassifying(false);
    }
  };

  const handleRefresh = () => {
    loadTransactions();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // One count + total-amount figure per card, all computed off the current
  // page's transactions (same set the original two summary cards used).
  const cardStats = useMemo(() => {
    return STATUS_CARDS.map(card => {
      const matching = transactions.filter(txn => matchesCard(txn.transaction_purpose, card.key));
      const total = matching.reduce((sum, txn) => sum + parseFloat(txn.amount || 0), 0);
      return { ...card, count: matching.length, total };
    });
  }, [transactions]);

  // What actually shows in the table: transactions narrowed to whichever
  // card is currently selected.
  const filteredTransactions = useMemo(
    () => transactions.filter(txn => matchesCard(txn.transaction_purpose, cardFilter)),
    [transactions, cardFilter]
  );

  return (
    <div className="pb-32">
      {/* Status cards - All Outgoing Payments / Classified / Unclassified */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = cardFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => setCardFilter(card.key)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.count}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatCurrency(card.total)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Bank to Client Transactions
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Classify outgoing transactions to members
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters — date range only, as requested */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Past 45 Days - default range */}
          <button
            onClick={handlePast45Days}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'past45'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Past 45 Days
          </button>

          {/* Month Selector Button */}
          <button
            onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
            className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
              activeDatePreset === 'month'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <span>
              {selectedMonth !== '' ? (() => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const [year, monthIndex] = selectedMonth.split('-');
                return `${monthNames[parseInt(monthIndex)]} ${year}`;
              })() : 'Select Month'}
            </span>
            <Calendar className="w-4 h-4 ml-2" />
          </button>

          {/* Custom Date Button */}
          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'custom'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Error banner */}
        {error && !loading && (
          <div className={`mx-5 mt-4 rounded-lg p-4 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between gap-3">
              <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
              <button
                onClick={loadTransactions}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 shrink-0"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Loading transactions...
            </span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Transaction ID
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Date &amp; Time
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Member
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Phone
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Amount
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Purpose
                    </th>
                    <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {filteredTransactions.map((txn) => {
                    const isPending = txn.transaction_purpose === 'pending_classification';
                    return (
                      <tr
                        key={txn.id}
                        onClick={() => isPending && handleOpenClassifyDialog(txn)}
                        className={`${isPending ? 'cursor-pointer' : ''} ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-6 py-4">
                          <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                            {txn.transaction_id}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {formatDate(txn.transaction_time)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                            {txn.member_name || txn.recipient_name}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            {txn.member_id || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {txn.formatted_phone || txn.recipient_phone}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {formatCurrency(txn.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white"
                            style={{ backgroundColor: getPurposeColor(txn.transaction_purpose) }}
                          >
                            {getPurposeLabel(txn.transaction_purpose)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {isPending ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenClassifyDialog(txn);
                              }}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <ClipboardCheck className="w-4 h-4" />
                              Classify
                            </button>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Classified
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredTransactions.length === 0 && (
              <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className="text-lg">No transactions found.</p>
              </div>
            )}

            {/* Footer count + pagination */}
            <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
              <span>
                Showing {filteredTransactions.length} of {transactions.length} loaded transactions
                {pagination.total ? ` (${pagination.total} total)` : ''}
              </span>

              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className={`px-3 py-1.5 border rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDark ? 'border-gray-700 text-gray-200 hover:border-blue-500' : 'border-gray-300 text-gray-900 hover:border-blue-500'
                    }`}
                  >
                    Previous
                  </button>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className={`px-3 py-1.5 border rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDark ? 'border-gray-700 text-gray-200 hover:border-blue-500' : 'border-gray-300 text-gray-900 hover:border-blue-500'
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setActiveDatePreset('month');
                    setMonthMenuAnchor(null);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearDateFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearDateFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Classification Dialog — kept as MUI. This is working business logic
          (exact-match suggestions, loan/withdrawal matching) rather than
          page layout, so it wasn't converted to the Tailwind card/table
          look; only the page around it was redesigned. */}
      <Dialog
        open={showClassifyDialog}
        onClose={() => !classifying && setShowClassifyDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Classify Transaction</Typography>
            <IconButton onClick={() => !classifying && setShowClassifyDialog(false)} disabled={classifying}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedTransaction && (
            <Box>
              {/* Transaction Details */}
              <Box sx={{ p: 2, mb: 3, backgroundColor: 'action.hover', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedTransaction.transaction_id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatCurrency(selectedTransaction.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Member</Typography>
                    <Typography variant="body1">{selectedTransaction.member_name || selectedTransaction.recipient_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{selectedTransaction.formatted_phone || selectedTransaction.recipient_phone}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Loading Suggestions */}
              {loadingSuggestions && (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              )}

              {/* Suggestions */}
              {suggestions && !loadingSuggestions && (
                <Box mb={3}>
                  {/* Exact Matches */}
                  {suggestions.summary.exact_matches > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Found {suggestions.summary.exact_matches} exact match(es)!
                      </Typography>
                    </Alert>
                  )}

                  {/* Ready Loans */}
                  {suggestions.ready_for_action.loans.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        <LoanIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Ready for Disbursement Loans:
                      </Typography>
                      <List dense>
                        {suggestions.ready_for_action.loans.map((loan) => (
                          <ListItem
                            key={loan.loan_id}
                            sx={{
                              backgroundColor: loan.match_status === 'exact_match' ? 'success.lighter' : 'action.hover',
                              borderRadius: 1,
                              mb: 1,
                              border: 1,
                              borderColor: loan.match_status === 'exact_match' ? 'success.main' : 'divider',
                            }}
                          >
                            <ListItemIcon>
                              <LoanIcon color={loan.match_status === 'exact_match' ? 'success' : 'action'} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Loan #${loan.loan_id} - Net: ${formatCurrency(loan.net_disbursement)}`}
                              secondary={
                                loan.amount_difference > 0
                                  ? `Difference: ${formatCurrency(loan.amount_difference)}`
                                  : 'Exact match!'
                              }
                            />
                            {loan.match_status === 'exact_match' && (
                              <CheckCircleIcon color="success" />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Approved Withdrawals */}
                  {suggestions.ready_for_action.withdrawals.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        <SavingsIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        Approved Withdrawal Requests:
                      </Typography>
                      <List dense>
                        {suggestions.ready_for_action.withdrawals.map((withdrawal) => (
                          <ListItem
                            key={withdrawal.request_id}
                            sx={{
                              backgroundColor: withdrawal.match_status === 'exact_match' ? 'success.lighter' : 'action.hover',
                              borderRadius: 1,
                              mb: 1,
                              border: 1,
                              borderColor: withdrawal.match_status === 'exact_match' ? 'success.main' : 'divider',
                            }}
                          >
                            <ListItemIcon>
                              <SavingsIcon color={withdrawal.match_status === 'exact_match' ? 'success' : 'action'} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${withdrawal.withdrawal_type.toUpperCase()} Withdrawal #${withdrawal.request_id} - Release: ${formatCurrency(withdrawal.release_amount)}`}
                              secondary={
                                withdrawal.amount_difference > 0
                                  ? `Difference: ${formatCurrency(withdrawal.amount_difference)}`
                                  : 'Exact match!'
                              }
                            />
                            {withdrawal.match_status === 'exact_match' && (
                              <CheckCircleIcon color="success" />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Other Suggestions/Warnings */}
                  {suggestions.other_suggestions && suggestions.other_suggestions.length > 0 && (
                    <Box mb={2}>
                      {suggestions.other_suggestions.map((suggestion, index) => (
                        <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                          <Typography variant="body2">{suggestion.message}</Typography>
                        </Alert>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Classification Form */}
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Classification Type</InputLabel>
                  <Select
                    value={classificationData.classification_type}
                    label="Classification Type"
                    onChange={(e) => setClassificationData({
                      ...classificationData,
                      classification_type: e.target.value,
                      loan_id: null,
                      withdrawal_request_id: null,
                    })}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem value="loan_disbursement">Long Term Loan Disbursement</MenuItem>
                    <MenuItem value="client_withdrawal">Savings Withdrawal</MenuItem>
                    <MenuItem value="refund">Refund</MenuItem>
                    <MenuItem value="emergency_payout">Emergency Payout</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                {/* Loan Selection */}
                {classificationData.classification_type === 'loan_disbursement' && suggestions?.ready_for_action.loans.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Loan</InputLabel>
                    <Select
                      value={classificationData.loan_id || ''}
                      label="Select Loan"
                      onChange={(e) => setClassificationData({
                        ...classificationData,
                        loan_id: e.target.value
                      })}
                    >
                      {suggestions.ready_for_action.loans.map((loan) => (
                        <MenuItem key={loan.loan_id} value={loan.loan_id}>
                          Loan #{loan.loan_id} - Net: {formatCurrency(loan.net_disbursement)}
                          {loan.match_status === 'exact_match' && ' ✓'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Withdrawal Selection */}
                {classificationData.classification_type === 'client_withdrawal' && suggestions?.ready_for_action.withdrawals.length > 0 && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Withdrawal Request</InputLabel>
                    <Select
                      value={classificationData.withdrawal_request_id || ''}
                      label="Select Withdrawal Request"
                      onChange={(e) => setClassificationData({
                        ...classificationData,
                        withdrawal_request_id: e.target.value
                      })}
                    >
                      {suggestions.ready_for_action.withdrawals.map((withdrawal) => (
                        <MenuItem key={withdrawal.request_id} value={withdrawal.request_id}>
                          {withdrawal.withdrawal_type.toUpperCase()} #{withdrawal.request_id} - Release: {formatCurrency(withdrawal.release_amount)}
                          {withdrawal.match_status === 'exact_match' && ' ✓'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Notes */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes (Optional)"
                  value={classificationData.notes}
                  onChange={(e) => setClassificationData({
                    ...classificationData,
                    notes: e.target.value
                  })}
                  placeholder="Add any additional notes about this classification..."
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowClassifyDialog(false)} disabled={classifying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleClassify}
            disabled={classifying || !classificationData.classification_type}
            startIcon={classifying ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {classifying ? 'Classifying...' : 'Classify & Process'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ConsolidatedWithdrawals;